import { MdOpenInNew } from "react-icons/md";
import { Button } from "components/ui";
import { useTranslation } from "hooks/useTranslation";

/**
 * Opens a tenant-facing setup guide in Help → Technical Docs, in a new tab.
 *
 * Put one next to anything a user has to fill in with values from a third
 * party (SMTP credentials, a bot token, an access token) — the guide covers
 * where those values come from, which is the part the form itself cannot
 * explain.
 *
 * **Why a new tab and not in-app navigation or a nested modal:** most of these
 * links sit inside the "Add Telegram" / "Add Slack" dialog. Navigating in-app
 * would unmount the dialog and throw away whatever the user has already typed,
 * and a second dialog stacked over the editor cannot be read while typing into
 * the form underneath it. The user also has to leave for BotFather /
 * api.slack.com anyway, so a tab they can keep side by side is the useful
 * shape. Rendering a real anchor (rather than `window.open`) keeps
 * middle-click, ctrl-click and "copy link address" working.
 *
 * `section` is the slug of an `##`/`###` heading in that document, so the
 * link lands on the provider the user is actually adding rather than at the
 * top of a long page. Slugs are lowercased with non-alphanumerics collapsed
 * to `-`, e.g. "Help Scout" → `help-scout`.
 */

interface SetupGuideLinkProps {
  /** Document id as registered in TechnicalDocs/manifest.json. */
  doc: string;
  /** Heading slug to scroll to. Omit to open at the top. */
  section?: string;
  /** Override label. Defaults to t('app.common.setupGuide'). */
  label?: string;
  intent?: "secondary" | "ghost";
  size?: "xs" | "sm" | "md";
}

export const SetupGuideLink = ({
  doc,
  section,
  label,
  intent = "secondary",
  size = "sm",
}: SetupGuideLinkProps) => {
  const { t } = useTranslation();
  const text = label ?? t("app.common.setupGuide");

  const params = new URLSearchParams({ tab: "technical-docs", doc });
  if (section) params.set("section", section);
  const href = `/admin/help?${params.toString()}`;

  return (
    <Button asChild intent={intent} size={size}>
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        title={t("app.common.opensInNewTab")}
      >
        <MdOpenInNew />
        {text}
      </a>
    </Button>
  );
};
